import React, { useContext, useEffect } from 'react';
import {
  Checkbox,
  ColorPicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  message,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  handleErrorInputMessage,
  handleInputError,
} from '../../../core/utils/errorUtils';
import { FormCard, FormContainer, FormSectionTitle } from '@leedpulse-monorepo/components';
import {
  creditTypeAsString,
  EProductType,
  ContextStatus,
  PlanFeaturesType,
  Currency,
  ECreditType,
} from '@leedpulse-monorepo/enums';
import { ICreditMap } from '@leedpulse-monorepo/interfaces';

import PlanCreationFormContext from '../../contexts/forms/plans/PlanCreationFormContext';
import { PLANS_PAGE_PATH } from '../../pages/plans/PlansPage';
import DebouncedPlansSelect from './select/DebouncedPlansSelect';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';

const { Option } = Select;

const PlanCreationForm: React.FC = () => {
  const navigate = useNavigate();
  const formContext = useContext(PlanCreationFormContext);
  const authContext = useContext(AuthenticationContext);

  useEffect(() => {
    switch (formContext.status) {
      case ContextStatus.SUCCESS:
        message.success('Plan créé avec succès');
        navigate(PLANS_PAGE_PATH);
        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la création du plan');
        break;
      default:
        break;
    }
  }, [formContext.status]);

  return (
    <FormCard
      title="Nouveau Plan"
      showTitle={false}
      onCreated={() => formContext.submit()}
      isCreateLoading={formContext.status === ContextStatus.LOADING}
    >
      <Form layout="vertical">
        <FormSectionTitle title="Informations générales" />
        <FormContainer>
          <Form.Item
            required
            label="ID"
            validateStatus={handleInputError(
              formContext.id,
              formContext.status
            )}
            help={handleErrorInputMessage(formContext.id, formContext.status)}
          >
            <Input
              value={formContext.id}
              onChange={(e) => {
                // Only scack case, only [a-b] and [0-9] allowed
                // Remove spaces
                e.target.value = e.target.value.replace(/\s/g, '');
                // Accept only A to Z, 0 to 9 and -
                e.target.value = e.target.value.replace(/[^a-z0-9-]/g, '');
                formContext.onIdChange(e.target.value);
              }}
            />
          </Form.Item>

          <Form.Item
            required
            label="Nom"
            validateStatus={handleInputError(
              formContext.name,
              formContext.status
            )}
            help={handleErrorInputMessage(formContext.name, formContext.status)}
          >
            <Input
              value={formContext.name}
              onChange={(e) => formContext.onNameChange(e.target.value)}
            />
          </Form.Item>

          <Form.Item
            required
            label="Couleur"
            validateStatus={handleInputError(
              formContext.color,
              formContext.status
            )}
            help={handleErrorInputMessage(
              formContext.color,
              formContext.status
            )}
          >
            <Flex gap={8} align="center">
              <ColorPicker
                value={formContext.color}
                onChange={(color) =>
                  formContext.onColorChange(color.toHexString())
                }
              />

              <Input readOnly value={formContext.color} />
            </Flex>
          </Form.Item>
        </FormContainer>

        <FormContainer>
          <Form.Item
            label="Crédits"
          >
            <Flex gap={8}>
              {
                (
                  formContext.product === EProductType.LEEDPULSE ?
                    Object.keys(ECreditType).filter((key) => key !== ECreditType.leedbucks) :
                    [ECreditType.leedbucks]
                ).map((key) => (
                  <Flex key={key} gap={8} align="center">
                    <span>
                      {creditTypeAsString(ECreditType[key as ECreditType])}
                    </span>
                    <InputNumber
                      style={{ width: '100%' }}
                      value={
                        (formContext.credits as ICreditMap)[key as ECreditType]
                      }
                      onChange={(value) =>
                        formContext.onCreditsChange(
                          ECreditType[key as keyof typeof ECreditType],
                          value ?? 0
                        )
                      }
                      step={1}
                      min={0}
                    />
                  </Flex>
                ))
              }
            </Flex>
          </Form.Item>

          <Form.Item
            label="Prix"
            extra={
              <span>Écriture formatée : {formContext.price.formatted}</span>
            }
            tooltip="Déprécié, utilisation du prix Stripe linké à la place"
          >
            <Flex gap={8}>
              <InputNumber
                value={formContext.price.value}
                style={{ width: '100%' }}
                onChange={(value) =>
                  formContext.onPriceChange({ value: value ?? 0 })
                }
                step={0.01}
                min={0}
                decimalSeparator="."
              />

              <Select
                value={formContext.price.currency}
                onChange={(value) =>
                  formContext.onPriceChange({ currency: value as Currency })
                }
              >
                {Object.keys(Currency).map((key) => (
                  <Option key={key} value={Currency[key as Currency]}>
                    {Currency[key as Currency]}
                  </Option>
                ))}
              </Select>
            </Flex>
          </Form.Item>

          <Form.Item label="Stripe Price ID">
            <Input
              value={formContext.price.stripe_price_id}
              onChange={(e) =>
                formContext.onPriceChange({ stripe_price_id: e.target.value })
              }
            />
          </Form.Item>

          <Form.Item label="Stripe Tax ID">
            <Input
              value={formContext.price.stripe_tax_id ?? ''}
              onChange={(e) =>
                formContext.onPriceChange({ stripe_tax_id: e.target.value })
              }
              placeholder="txr_... (optionnel)"
            />
          </Form.Item>
        </FormContainer>

        <FormContainer>
          <Form.Item label="Produit">
            <Select
              value={formContext.product}
              onChange={(value) =>
                formContext.onProductChange(value as EProductType)
              }
            >
              <Option key={EProductType.LEEDPULSE} value={EProductType.LEEDPULSE}>
                {EProductType.LEEDPULSE}
              </Option>
              <Option key={EProductType.LEEDFLOW} value={EProductType.LEEDFLOW}>
                {EProductType.LEEDFLOW}
              </Option>
            </Select>
          </Form.Item>

          {formContext.product === EProductType.LEEDFLOW && (
            <>
              <Form.Item label="Downgradable vers">
                <DebouncedPlansSelect
                  auth={authContext.authentication}
                  product={EProductType.LEEDFLOW}
                  mode="multiple"
                  showSearch
                  customOptions={false}
                  notAllowedOptions={[...formContext.upgradable_to]}
                  placeholder="Rechercher un plan..."
                  value={formContext.downgradable_to}
                  onChange={(value) => formContext.onDowngradableToChange(value as string[])}
                />
              </Form.Item>

              <Form.Item label="Upgradable vers">
                <DebouncedPlansSelect
                  auth={authContext.authentication}
                  product={EProductType.LEEDFLOW}
                  mode="multiple"
                  showSearch
                  customOptions={false}
                  notAllowedOptions={[...formContext.downgradable_to]}
                  placeholder="Rechercher un plan..."
                  value={formContext.upgradable_to}
                  onChange={(value) => formContext.onUpgradableToChange(value as string[])}
                />
              </Form.Item>

              <Form.Item label="Prix hors forfait">
                <Flex gap={8}>
                  {[ECreditType.leedbucks].map((key) => (
                    <Flex key={key} gap={8} align="center">
                      <span title={creditTypeAsString(key, false)}>
                        {creditTypeAsString(key)}
                      </span>
                      <InputNumber
                        style={{ width: '100%' }}
                        onChange={(value) =>
                          formContext.onPaygChange(
                            key as ECreditType,
                            value ?? 0
                          )
                        }
                        value={formContext.payg[key as ECreditType]}
                        step={0.01}
                        min={0}
                      />
                    </Flex>
                  ))}
                </Flex>
              </Form.Item>
            </>
          )}
        </FormContainer>

        <FormContainer>
          <Form.Item label="Features">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8,
                maxHeight: '6.25rem',
                overflowY: 'auto',
              }}
            >
              {Object.keys(PlanFeaturesType).map((key) => (
                <Flex key={key} gap={8} align="center">
                  <Checkbox
                    checked={formContext.features[key as PlanFeaturesType]}
                    onChange={(e) =>
                      formContext.onFeaturesChange(
                        key as PlanFeaturesType,
                        e.target.checked
                      )
                    }
                  >
                    {PlanFeaturesType[key as PlanFeaturesType]}
                  </Checkbox>
                </Flex>
              ))}
            </div>
          </Form.Item>

          <Form.Item label="Récurrence">
            <Checkbox
              defaultChecked={formContext.recurrence}
              onChange={(e) => formContext.onRecurrenceChange(e.target.checked)}
            >
              Récurrence
            </Checkbox>
          </Form.Item>

          <Form.Item label="Réinitialisation des crédits">
            <Checkbox
              defaultChecked={formContext.recurrence_credits_reset}
              onChange={(e) =>
                formContext.onRecurrenceCreditsResetChange(e.target.checked)
              }
            >
              Réinitialisation des crédits
            </Checkbox>
          </Form.Item>
        </FormContainer>
      </Form>
    </FormCard>
  );
};

export default PlanCreationForm;
